import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Paper,
  Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import EngineeringIcon from "@mui/icons-material/Engineering";
import TimelineIcon from "@mui/icons-material/Timeline";
import PaidIcon from "@mui/icons-material/Paid";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StraightenIcon from "@mui/icons-material/Straighten";

import img1 from "../../pictures/project1.jpg";
import img2 from "../../pictures/project2.jpg";
import img3 from "../../pictures/project3.jpg";
import img4 from "../../pictures/project4.jpg";

const projectData = [
  {
    id: 1,
    title: "Residential Independent Duplex",
    clientName: "Dr. Balachandran",
    location: "Nolambur",
    type: "Residential Duplex",
    status: "Ongoing",
    images: [img1, img2, img3, img4],
    area: "9000 Sq.ft",
    floors: "Stilt + 3 Floors",
    duration: "Ongoing (Est. 10-12 Months)",
    category: "Residential Independent Duplex",
    overview: "Premium residential independent duplex project currently under execution at Nolambur for Dr. Balachandran. The project spans approximately 9000 sq.ft with stilt + 3 floor configuration featuring structural construction, space planning, and premium residential execution.",
    features: [
      "Stilt + 3 Floor residential duplex",
      "9000 Sq.ft premium construction", 
      "Independent house design",
      "Ongoing structural development"
    ]
  },
  {
    id: 2,
    title: "Independent Duplex Residence", 
    clientName: "Mr. Sarath",
    location: "Thirumazhisai",
    type: "Independent Duplex",
    status: "Ongoing",
    images: [img2, img1, img4, img3],
    area: "5000 Sq.ft",
    floors: "Ground + 2 Floors",
    duration: "Ongoing (Est. 8-10 Months)",
    category: "Independent Duplex House",
    overview: "Independent duplex residence under construction at Thirumazhisai for Mr. Sarathi. The project covers approximately 5600 sq.ft with ground + 2 floor arrangement focusing on practical residential planning, structural quality, and modern duplex living requirements.",
    features: [
      "Ground + 2 Floor independent duplex",
      "5600 Sq.ft residential construction",
      "Practical space planning",
      "Modern duplex living design"
    ]
  },
  {
    id: 3,
    title: "Commercial Office & Renovation",
    clientName: "Veyem Engineering Services",
    location: "Ambattur", 
    type: "Commercial & Renovation",
    status: "Ongoing",
    images: [img3, img1, img2, img4],
    area: "Commercial Project",
    floors: "Office / Renovation Works",
    duration: "Ongoing (Multi-phase)",
    category: "Commercial Office + House Renovation",
    overview: "Ongoing commercial office works and house renovation project for Neyam Engineering at Ambattur. The project includes coordinated execution for both commercial office construction and residential improvement requirements with professional site management.",
    features: [
      "Commercial office construction",
      "House renovation works",
      "Multi-disciplinary project execution", 
      "Professional site coordination"
    ]
  }
];

function ProjectDetails() {
  const { id } = useParams();
  // const location = useLocation();
  const project = projectData.find((p) => p.id === parseInt(id));
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState("");

  useEffect(() => {
    if (project?.images?.length > 0) {
      setActiveImage(project.images[0]);
    }
  }, [project]);

  if (!project) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography variant="h5" textAlign="center" color="error">
          Project Not Found
        </Typography>
      </Container>
    );
  }

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          height: 350,
          background: `linear-gradient(rgba(0, 3, 100, 0.85), rgba(10, 10, 56, 0.7)), url(${activeImage || project.images[0]})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          textAlign: "center",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Box>
          <Typography variant="h3" fontWeight="bold" mb={1}>
            {project.title}
          </Typography>
          <Chip 
            label={project.status} 
            color="warning" 
            variant="filled"
            sx={{ fontSize: "1rem", fontWeight: "bold" }}
          />
        </Box>
      </Box>

      <Container sx={{ py: 8 }}>
        {/* IMAGE GALLERY */}
        <Box mb={6}>
          <Box
            component="img"
            src={activeImage || project.images[0]}
            sx={{
              width: "100%",
              height: 450,
              objectFit: "cover",
              borderRadius: 4,
              boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              transition: "0.5s ease"
            }}
          />

          {/* Thumbnails */}
          <Box display="flex" gap={2} mt={3} justifyContent="center">
            {project.images.map((img, i) => (
              <Box
                key={i}
                component="img"
                src={img}
                onClick={() => setActiveImage(img)}
                sx={{
                  width: 110,
                  height: 80,
                  cursor: "pointer",
                  borderRadius: 2,
                  border: activeImage === img ? "4px solid #03023f" : "2px solid #e0e0e0",
                  boxShadow: activeImage === img ? "0 8px 20px rgba(18, 2, 77, 0.77)" : "none",
                  transition: "all 0.3s ease",
                  objectFit: "cover",
                  "&:hover": {
                    transform: "scale(1.05)",
                    borderColor: "#020d46"
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        <Grid container spacing={5}>
          {/* LEFT - PROJECT DETAILS */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 5, borderRadius: 4, background: "linear-gradient(145deg, #fff 0%, #faf9f7 100%)" }}>
              <Typography variant="h4" fontWeight="bold" mb={3} color="#1a1a1a">
                Project Overview
              </Typography>

              <Box mb={4}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <LocationOnIcon sx={{ color: "#140342", mr: 1 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Location
                      </Typography>
                    </Box>
                    <Typography variant="body1">{project.location}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="h6" fontWeight="bold">
                        Client
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="600" color="#f57c00">
                      {project.clientName}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={4}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Specifications
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography><strong>Area:</strong> {project.area}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography><strong>Floors:</strong> {project.floors}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography><strong>Status:</strong> {project.status}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography><strong>Type:</strong> {project.category}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Current Works
                </Typography>
                {project.features.map((feature, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <StraightenIcon sx={{ color: "#0b054c", mr: 1, fontSize: 20 }} />
                    <Typography>{feature}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT - TRANSPARENCY */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 4, borderRadius: 4, height: "fit-content" }}>
              <Typography variant="h5" fontWeight="bold" mb={4} color="#1a1a1a">
                100% Transparent Construction
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <TimelineIcon sx={{ fontSize: 48, color: "#0b054c", mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold">Live Progress</Typography>
                    <Typography variant="body2" color="text.secondary">Real-time updates</Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <PaidIcon sx={{ fontSize: 48, color: "#0b054c", mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold">Cost Control</Typography>
                    <Typography variant="body2" color="text.secondary">Budget tracking</Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <EngineeringIcon sx={{ fontSize: 48, color: "#0b054c", mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold">Expert Team</Typography>
                    <Typography variant="body2" color="text.secondary">Skilled engineers</Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <HomeIcon sx={{ fontSize: 48, color: "#0b054c", mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold">Quality Materials</Typography>
                    <Typography variant="body2" color="text.secondary">Premium standards</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* PROJECT TIMELINE */}
        <Box mt={10}>
          <Typography variant="h4" textAlign="center" mb={6} fontWeight="bold">
            Current Progress Timeline
          </Typography>

          <Grid container justifyContent="center" spacing={3}>
            {["Site Preparation", "Foundation Work", "Structural Phase", "Ongoing Construction"].map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} textAlign="center">
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: "linear-gradient(145deg, #fff 0%, #f8f9fa 100%)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    transition: "all 0.4s ease",
                    border: "1px solid rgba(245,124,0,0.1)",
                    "&:hover": {
                      transform: "translateY(-10px) scale(1.02)",
                      boxShadow: "0 20px 40px rgba(245,124,0,0.15)"
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="#0b054c" mb={2}>
                    {step}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {index < 2 ? "Completed" : "In Progress"}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA */}
        <Box textAlign="center" mt={12} py={6} sx={{ background: "linear-gradient(145deg, #f5f5ff 0%, #f1f0ff 100%)", borderRadius: 4 }}>
          <Typography variant="h5" fontWeight="bold" mb={3} color="#1a1a1a">
            Ready for Your Dream Project?
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4} sx={{ maxWidth: 600, mx: "auto" }}>
            Get a free consultation for your residential, commercial, or renovation project just like this one.
          </Typography>
          <Button
  variant="contained"
  size="large"
  onClick={() => navigate('/contact-us')}
  sx={{
    background: "linear-gradient(45deg, #0b054c 0%, #0b054c 100%)",
    px: 6,
    py: 1.5,
    borderRadius: 50,
    fontSize: "1.1rem",
    fontWeight: "bold",
    boxShadow: "0 10px 30px rgba(41, 0, 245, 0.4)",
    "&:hover": {
      background: "linear-gradient(45deg, #19035c 0%, #2200ff 100%)",
      boxShadow: "0 15px 40px rgba(45, 0, 245, 0.5)",
      transform: "translateY(-2px)"
    }
  }}
>
  Start Your Project
</Button>

        </Box>
      </Container>
    </Box>
  );
}

export default ProjectDetails;
